import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, Store, MapPin, BadgeCheck, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, { withCredentials: true });
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (shopId) => {
    try {
      await axios.delete(`${API}/favorites/${shopId}`, { withCredentials: true });
      setFavorites(prev => prev.filter(f => f.id !== shopId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const markAsSeen = async (shopId) => {
    try {
      await axios.put(`${API}/favorites/${shopId}/seen`, {}, { withCredentials: true });
      setFavorites(prev => prev.map(f => 
        f.id === shopId ? { ...f, new_products_count: 0 } : f
      ));
    } catch (error) {
      console.error('Error marking as seen:', error);
    }
  };

  const handleShopClick = async (shop) => {
    if (shop.new_products_count > 0) {
      await markAsSeen(shop.id);
    }
    navigate(`/negozio/${shop.id}`);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24" data-testid="favorites-page">
        <header className="sticky top-0 z-50 bg-white border-b border-slate-100">
          <div className="flex items-center px-4 py-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 hover:bg-slate-100 rounded-full"
            >
              <ArrowLeft className="w-6 h-6 text-slate-700" />
            </button>
            <h1 className="font-outfit font-semibold text-lg text-slate-800 ml-2">
              Negozi preferiti
            </h1>
          </div>
        </header>

        <main className="px-4 py-8 max-w-lg mx-auto text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="font-outfit font-semibold text-xl text-slate-800 mb-2">
            Accedi per vedere i tuoi preferiti
          </h2>
          <p className="text-slate-500 mb-6">
            Salva i negozi che preferisci e ricevi notifiche sui nuovi prodotti
          </p>
          <Button onClick={login} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8">
            Accedi con Google
          </Button>
        </main>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="favorites-page">
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100">
        <div className="flex items-center px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 hover:bg-slate-100 rounded-full"
          >
            <ArrowLeft className="w-6 h-6 text-slate-700" />
          </button>
          <h1 className="font-outfit font-semibold text-lg text-slate-800 ml-2">
            Negozi preferiti
          </h1>
        </div>
      </header>

      <main className="px-4 py-4 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : favorites.length > 0 ? (
          <div className="space-y-3">
            {favorites.map((shop) => (
              <div
                key={shop.id}
                className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleShopClick(shop)}
                data-testid={`favorite-shop-${shop.id}`}
              >
                {/* Logo */}
                {shop.logo ? (
                  <img src={shop.logo} alt={shop.nome} className="w-14 h-14 rounded-xl object-cover" />
                ) : (
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Store className="w-7 h-7 text-blue-600" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-800 truncate">{shop.nome}</h3>
                    {shop.email_verified && (
                      <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-slate-500 truncate">{shop.indirizzo}, {shop.citta}</p>
                  
                  {/* New products badge */}
                  {shop.new_products_count > 0 && (
                    <span className="inline-flex items-center gap-1 mt-1 bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      <Bell className="w-3 h-3" />
                      {shop.new_products_count} nuov{shop.new_products_count === 1 ? 'o' : 'i'} prodott{shop.new_products_count === 1 ? 'o' : 'i'}
                    </span>
                  )}
                </div>

                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(shop.id);
                  }}
                  className="p-2 hover:bg-slate-100 rounded-full flex-shrink-0"
                  data-testid={`remove-favorite-${shop.id}`}
                >
                  <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl">
            <Heart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-outfit font-semibold text-slate-700 mb-2">
              Nessun negozio salvato
            </h3>
            <p className="text-slate-500 text-sm mb-4">
              Esplora i negozi e salva i tuoi preferiti
            </p>
            <Button onClick={() => navigate('/scopri')} variant="outline" className="rounded-xl">
              Scopri negozi
            </Button>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
